import React, { useContext, useMemo } from "react";
import { getLocale, useIntl } from "umi";
import moment from "moment";

import { useSelectorMap, useWeekDay } from "@/pages/dashboard/utils/customHook";
import CardBox from "@/pages/dashboard/components/CardBox";
import BarChart from "@/pages/dashboard/components/Chart/components/BarChart";
import LineChart from "@/pages/dashboard/components/Chart/components/LineChart";
import { formatDataByChartType, xAxisHighLight } from "@/pages/dashboard/components/Chart/_config/plug";
import { unitConversion, formatSmallValue, formatSmallPercent, commonValueFormatter } from "@/pages/dashboard/utils/utils";
import { getEchartTooltipText } from "@/pages/dashboard/components/EchartsBubble";
import { ICountryComparisonChart } from "./type";
import { FilterContext } from "@/pages/dashboard/components/PageContainer/context";
import { dynamicString } from "@home/utils/dynamicString";
import dataFormat from "@home/utils/dbDataFormat/dataFormat";

const CountryComparisonChart: React.FC<ICountryComparisonChart> = (props) => {
  const { selectedMetrics, data } = props || {};
  const { country_line_chart: countryLineChart } = data || {};

  const { filterVal } = useContext(FilterContext);
  const { timeType = "Day" } = filterVal || {};

  const chartData = countryLineChart?.data?.line_chart || [];
  const isError = !!countryLineChart?.errorMsg;
  const isLoading = countryLineChart?.loading;

  const { locale, formatMessage } = useIntl();
  const isEn = locale === 'en-US';

  const { getWeekDayStr } = useWeekDay();
  const { getSelectorText } = useSelectorMap();

  // 解析模板字符串（支持 metricsInfo 等数据源）
  const resolveTemplate = (template?: string): string => {
    if (!template) return '';
    return dynamicString(template, data) || '';
  };

  // 解析指标名称（支持 metricsInfo 模板）
  const resolvedName = resolveTemplate(selectedMetrics?.name);
  const resolvedNameCn = resolveTemplate(selectedMetrics?.nameCn);
  // 解析说明文案
  const resolvedExplainEn = resolveTemplate(selectedMetrics?.explainEn);
  const resolvedExplainCn = resolveTemplate(selectedMetrics?.explainCn);
  // 解析格式化规则
  const resolvedFormatRule = resolveTemplate(selectedMetrics?.formatRule);

  // 指标值格式化：优先使用 formatRule（dynamicString + dataFormat），兼容原逻辑
  const formatValue = (value: string | number) => {
    if (value === null || value === undefined || value === '') return '-';
    if (resolvedFormatRule) {
      return dataFormat({ value, formatRule: resolvedFormatRule });
    }
    const { isPercent, isUserType, decimals } = selectedMetrics;
    if (isPercent) {
      return formatSmallPercent(value, 2);
    }
    if (isUserType) {
      return commonValueFormatter('int', value, 0);
    }
    return commonValueFormatter('decimals', value, decimals);
  };
  
  const formatterFunc = (params: any) => {
    const tooltipItems = params?.map((item: any, index: any) => {
      const metricsNum = formatValue(item.value);
        
      return {
        color: params[index].color,
        name: params[index].seriesName,
        metricsNum,
      };
    });

    let title: string;

    if (moment(params?.[0]?.name).isValid()) {
      title = timeType === 'Month' 
        ? moment(params?.[0]?.name).format('YYYY-MM') 
        : getWeekDayStr(params?.[0]?.name, timeType);
    } else {
      title = params?.[0]?.name;
    }

    return getEchartTooltipText({
      title,
      data: tooltipItems,
      isSort: true,
      isShowTotal: true,
    });
  };

  const yAxisLabelFormat = (value: any) => {
    const formatLabel = value ? unitConversion(value, true) : value;
    const num = value < 1 && value > 0 ? parseFloat(value.toFixed(3)) : formatLabel;
    return num;
  };

  const setOtherConfigFunc = (opt: any) => {
    let updatedOpt = opt;

    const { isPercent } = selectedMetrics;
    // opt.series = testMinValue(opt,0.00001);
    if (updatedOpt?.legend) {
      updatedOpt.legend.selected = {
        // 图例默认不显示全球和总数
        ...updatedOpt.legend.selected,
        [formatMessage({ id: `comparison_analysis_country_comparison_key_total` })]: false,
        [formatMessage({ id: `comparison_analysis_country_comparison_key_global` })]: false,
      };
    }
  
    updatedOpt.yAxis.axisLabel.formatter = (value: any) => {
      if (!isPercent) {
        if(value>0 && value<1 && selectedMetrics?.resName === 'arpu') {
          return formatSmallValue(value, 'decimals', 2);
        }
        return yAxisLabelFormat(value);
      }

      return formatSmallPercent(+value, 2, true);
    };

    if (updatedOpt?.xAxis?.[0]?.axisLabel) {
      updatedOpt.xAxis[0].axisLabel.formatter = (value: any) => {
        if (moment(value).isValid()) {
          return timeType === 'Month' ? moment(value).format('YYYY-MM') : moment(value).format('MM-DD');
        }

        return value;
      };
    }
    updatedOpt = xAxisHighLight(opt, {
      formatter: ({ value }: any) => timeType === 'Month' ? moment(value).format('YYYY-MM') : moment(value).format('MM-DD')
    });
    return updatedOpt;
  };

  const config = {
    bar: {
      formatterFunc,
      setOtherConfigFunc,
    },
    line: {
      formatterFunc,
      setOtherConfigFunc,
    },
  };

  const options = useMemo(() => {
    if (!chartData) return {};

    // 根据国家维度重新整合数据，每组数据都是一个国家
    const countryNames: any[] = [];
    chartData.forEach((item: any) => {
      if (!countryNames.find((e) => e === item.name)) {
        countryNames.push(item.name);
      }
    });
    const countryDatas: any[] = [];
    countryNames.forEach((item) => {
      countryDatas.push(chartData.filter((e: any) => e.name === item));
    });

    const chart = countryDatas.map((country) => {
      const seriesData = (country || []).map((data: any) => {
        const x = moment(data.date).format('YYYY-MM-DD') || '';
        return {
          x,
          y: data?.value ? data.value : '',
        };
      });

      let name = country?.[0]?.name?.trim() || '';

      if (name === 'Total') {
        name = formatMessage({ id: 'comparison_analysis_country_comparison_key_total' });
      } else if (name === 'Global') {
        name = formatMessage({ id: 'comparison_analysis_country_comparison_key_global' });
      } else {
        name = getSelectorText('country', name);
      }

      return {
        name,
        barCategoryGap: '50%',
        data: seriesData,
        stack: 'total',
      };
    });

    return {
      chart,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, getLocale()]);

  const lineChartConfig = formatDataByChartType('line', options?.chart, config?.line);
  const barChartConfig = formatDataByChartType('bar', options?.chart, config?.bar);

  const renderList = [
    {
      type: 'barChart',
      svgType: 'column-chart',
      index: 1,
      content: formatMessage({ id: "common_bar_chart" }),
      component:
        <BarChart
          height={307}
          title={''}
          legendData={barChartConfig?.legend || []}
          xAxisData={barChartConfig?.xAxisData || []}
          series={barChartConfig?.series || []}
          formatterFunc={(params: any) => config.bar.formatterFunc(params)}
          setOtherConfigFunc={(opt: any) => config.bar.setOtherConfigFunc(opt)}
        />
    },
    {
      type: 'lineChart',
      svgType: 'discount-chart',
      index: 2,
      content: formatMessage({ id: "common_line_chart" }),
      component:
        <LineChart
          height={307}
          title={''}
          legendData={lineChartConfig?.legend || []}
          xAxisData={lineChartConfig?.xAxisData || []}
          series={lineChartConfig?.series || []}
          formatterFunc={(params: any) => config.line.formatterFunc(params)}
          setOtherConfigFunc={(opt: any) => config.line.setOtherConfigFunc(opt)}
        />
      ,
    },
  ];

  return (
    <>
      <CardBox
        className='register-table'
        height={307}
        titleCom={isEn ? resolvedName : resolvedNameCn}
        renderList={renderList}
        defaultAvtive={1}
        bordered={false}
        isLoading={isLoading}
        isError={isError}
        isNodata={chartData?.length === 0}
      />
    </>
  );
};

export default CountryComparisonChart;